/**
 * TilesetLibrary — Manages persistent tileset storage.
 *
 * Tilesets are stored as:
 *   tilesets/{id}/image.png  — the tileset image
 *   tilesets/{id}/meta.json  — metadata (name, tileSize, columns, etc.)
 *
 * Both bundled starter tilesets and user-imported ones flow through the same path.
 * Storage uses CloudStorage (R2 + IndexedDB fallback).
 */

import { TilesetCatalog } from '../generators/TilesetCatalog.js';

const STARTER_FLAG = 'tileforge_starter_provisioned';

/** Bundled tilesets that ship with TileForge (public/tilesets/bundled/) */
const BUNDLED_TILESETS = [
  { id: 'kenney-tiny-town',    file: 'kenney_tiny_town.png',             name: 'Tiny Town (Kenney)',           artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-tiny-battle',  file: 'kenney_tiny_battle.png',           name: 'Tiny Battle (Kenney)',         artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-tiny-ski',     file: 'kenney_tiny_ski.png',              name: 'Tiny Ski (Kenney)',            artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-minimap',      file: 'kenney_minimap.png',               name: 'Minimap (Kenney)',             artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-roguelike',    file: 'kenney_roguelike_characters.png',  name: 'Roguelike Characters (Kenney)', artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-desert-enemies', file: 'kenney_desert_enemies.png',      name: 'Desert Enemies (Kenney)',      artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-desert-players', file: 'kenney_desert_players.png',      name: 'Desert Players (Kenney)',      artist: 'Kenney', license: 'CC0' },
  { id: 'kenney-desert-weapons', file: 'kenney_desert_weapons.png',      name: 'Desert Weapons (Kenney)',      artist: 'Kenney', license: 'CC0' },
];

export class TilesetLibrary {
  constructor(cloudStorage, tilesetLoader) {
    this._storage = cloudStorage;
    this._loader = tilesetLoader;
    this._catalog = new TilesetCatalog();
    this._tilesets = new Map(); // id → meta
    this._onChangeCallbacks = [];
  }

  /** Load the library index from storage. Waits for CloudStorage IDB to be ready. */
  async init() {
    // CloudStorage.init() is async — wait for IDB to be available
    let retries = 0;
    while (!this._storage._db && retries < 20) {
      await new Promise(r => setTimeout(r, 50));
      retries++;
    }
    if (!this._storage._db) {
      console.warn('TilesetLibrary: CloudStorage IDB not ready after timeout');
    }
    await this._loadIndex();
  }

  /** Register a change listener. */
  onChange(fn) {
    this._onChangeCallbacks.push(fn);
    return () => {
      this._onChangeCallbacks = this._onChangeCallbacks.filter(cb => cb !== fn);
    };
  }

  _notifyChange() {
    for (const fn of this._onChangeCallbacks) fn();
  }

  /** List all tilesets in the library. Returns array of meta objects. */
  list() {
    return [...this._tilesets.values()].sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0));
  }

  /** Get a single tileset's metadata by ID. */
  get(id) {
    return this._tilesets.get(id) || null;
  }

  /**
   * Import a tileset from a File object into the library.
   * @returns {Object|null} the meta entry, or null if validation failed
   */
  async addFromFile(file) {
    const error = this._loader._validateFile(file);
    if (error) {
      this._loader._showError(error);
      return null;
    }

    const img = await this._loadImageFromFile(file);
    if (!img) return null;

    const dimError = this._loader._validateDimensions(img, file.name);
    if (dimError) {
      this._loader._showError(dimError);
      return null;
    }

    const id = this._generateId(file.name);
    const name = file.name.replace(/\.[^.]+$/, '');

    // Store image
    const arrayBuf = await file.arrayBuffer();
    await this._storage.upload(`tilesets/${id}/image.png`, new Blob([arrayBuf], { type: 'image/png' }), 'image/png');

    // Ask user for tile size
    const size = await this._loader._promptTileSize(img, file.name);
    if (!size) return null;

    // Build meta
    const tileW = size.tileW;
    const tileH = size.tileH;
    const columns = Math.floor(img.width / tileW);
    const rows = Math.floor(img.height / tileH);

    const meta = {
      id,
      name,
      tileSize: tileW,
      tileWidth: tileW,
      tileHeight: tileH,
      columns,
      tileCount: columns * rows,
      imageWidth: img.width,
      imageHeight: img.height,
      artist: null,
      license: null,
      addedAt: Date.now(),
      source: 'user-import',
    };

    // Auto-detect known tileset for attribution
    const catalogMatch = this._catalog.detectFromFilename(file.name);
    if (catalogMatch) {
      meta.artist = catalogMatch.artist;
      meta.license = catalogMatch.licenseType;
      meta.catalogId = catalogMatch.id;
    }

    await this._saveMeta(id, meta);
    this._tilesets.set(id, meta);
    this._notifyChange();
    return meta;
  }

  /**
   * Copy a bundled tileset into the user's library.
   * @param {Object} bundled — entry from BUNDLED_TILESETS
   */
  async addFromBundled(bundled) {
    if (this._tilesets.has(bundled.id)) return this._tilesets.get(bundled.id);

    // Fetch the bundled image from public/
    const response = await fetch(`tilesets/bundled/${bundled.file}`);
    if (!response.ok) {
      console.warn(`Failed to fetch bundled tileset: ${bundled.file}`);
      return null;
    }
    const blob = await response.blob();

    // Load as image to get dimensions
    const img = await this._loadImageFromBlob(blob);
    if (!img) return null;

    // Store in library
    await this._storage.upload(`tilesets/${bundled.id}/image.png`, blob, 'image/png');

    const tileW = this._loader.state.get('tileWidth');
    const tileH = this._loader.state.get('tileHeight');
    const columns = Math.floor(img.width / tileW);
    const rows = Math.floor(img.height / tileH);

    const meta = {
      id: bundled.id,
      name: bundled.name,
      tileSize: tileW,
      tileWidth: tileW,
      tileHeight: tileH,
      columns,
      tileCount: columns * rows,
      imageWidth: img.width,
      imageHeight: img.height,
      artist: bundled.artist,
      license: bundled.license,
      addedAt: Date.now(),
      source: 'bundled',
    };

    await this._saveMeta(bundled.id, meta);
    this._tilesets.set(bundled.id, meta);
    return meta;
  }

  /**
   * Provision starter tilesets for new users.
   * Only runs once — sets localStorage flag on completion.
   */
  async provisionStarterKit() {
    if (localStorage.getItem(STARTER_FLAG)) return false;

    let provisioned = 0;
    for (const bundled of BUNDLED_TILESETS) {
      try {
        const meta = await this.addFromBundled(bundled);
        if (meta) provisioned++;
      } catch (err) {
        console.warn(`Failed to provision ${bundled.name}:`, err.message);
      }
    }

    localStorage.setItem(STARTER_FLAG, Date.now().toString());
    this._notifyChange();
    console.log(`TilesetLibrary: provisioned ${provisioned} starter tilesets`);
    return true;
  }

  /**
   * Load a library tileset into the editor state.
   * @param {string} id — tileset ID
   * @returns {Object|null} the tileset data object added to state, or null
   */
  async loadIntoEditor(id) {
    const meta = this._tilesets.get(id);
    if (!meta) return null;

    const imagePath = `tilesets/${id}/image.png`;

    // Check if this tileset is already loaded in editor state
    const existing = this._loader.state.get('tilesets');
    const alreadyLoaded = existing.find(ts => ts.image === imagePath);
    if (alreadyLoaded) {
      return alreadyLoaded; // Already in editor, return existing entry
    }

    // Try loading from CloudStorage
    let img;
    try {
      img = await this._storage.loadImage(imagePath);
    } catch {
      // Fallback: if it's a bundled tileset, try loading from public/
      const bundled = BUNDLED_TILESETS.find(b => b.id === id);
      if (bundled) {
        img = await this._loadImageFromURL(`tilesets/bundled/${bundled.file}`);
      }
      if (!img) {
        console.warn(`TilesetLibrary: cannot load image for ${id}`);
        return null;
      }
    }

    // Use TilesetLoader's internal method to add to editor state
    // Pass the tileset's own tile size from its stored metadata
    const tileW = meta.tileWidth || meta.tileSize || this._loader.state.get('tileWidth');
    const tileH = meta.tileHeight || meta.tileSize || this._loader.state.get('tileHeight');
    return this._loader._createTilesetFromImage(img, meta.name, imagePath, tileW, tileH);
  }

  /** Remove a tileset from the library. */
  async remove(id) {
    await this._storage.delete(`tilesets/${id}/image.png`);
    await this._storage.delete(`tilesets/${id}/meta.json`);
    this._tilesets.delete(id);
    this._notifyChange();
  }

  /** Check if a tileset is in the library. */
  has(id) {
    return this._tilesets.has(id);
  }

  // --- Private helpers ---

  async _loadIndex() {
    // Scan LOCAL IndexedDB only — avoids R2 404 noise for signed-in users.
    // Cloud sync is handled separately by provisionStarterKit / upload.
    try {
      const localFiles = await this._storage._idbList();
      const metaPaths = localFiles
        .map(f => f.path)
        .filter(p => typeof p === 'string' && p.startsWith('tilesets/') && p.endsWith('/meta.json'));

      for (const path of metaPaths) {
        try {
          const result = await this._storage._idbGet(path);
          if (result && result.data) {
            const text = await (result.data instanceof Blob ? result.data.text() : new Blob([result.data]).text());
            const meta = JSON.parse(text);
            this._tilesets.set(meta.id, meta);
          }
        } catch (err) {
          console.warn(`Failed to load tileset meta: ${path}`, err);
        }
      }
    } catch (err) {
      console.warn('TilesetLibrary: failed to load index', err);
    }
  }

  async _saveMeta(id, meta) {
    const json = JSON.stringify(meta, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    await this._storage.upload(`tilesets/${id}/meta.json`, blob, 'application/json');
  }

  _generateId(filename) {
    const stem = filename.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${stem}-${Date.now().toString(36)}`;
  }

  _loadImageFromFile(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  _loadImageFromBlob(blob) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  _loadImageFromURL(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }
}

/** Exported for use in provisioning logic */
export { BUNDLED_TILESETS };
